import { useCallback } from 'react';
import { useLazyDownloadInvoicePdfQuery } from '../api/invoice/invoiceApiSlice';

/**
 * Save an issued invoice's archived PDF as `<number>.pdf`.
 *
 * One place for the booking's invoice section and the invoice list. Through
 * the authenticated client, not a navigation: the endpoint needs the bearer
 * token and `x-tenant`, and a 401 arriving as a blank tab would read as a
 * broken document.
 *
 * Rejects with the RTK Query error on a refusal, so the caller can show the
 * server's reason (`messageOf`) rather than a generic line.
 */
export const useInvoicePdfDownload = (): ((invoice: { id: string, number: string }) => Promise<void>) => {
    const [downloadPdf] = useLazyDownloadInvoicePdfQuery();
    return useCallback(async (invoice: { id: string, number: string }) => {
        const blob = await downloadPdf(invoice.id).unwrap();
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${invoice.number}.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
    }, [downloadPdf]);
};
